import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import { CopyIcon, XIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * One-time reveal of a signing secret.
 *
 * The API returns the secret only on create/rotate, so this dialog is the
 * operator's single chance to copy it. It deliberately cannot be reopened from
 * the list.
 */
async function copy(value: string, label: string) {
	try {
		await navigator.clipboard.writeText(value);
		toast.success(`${label} copied`);
	} catch {
		toast.error("Could not copy - select the text and copy manually");
	}
}

export function SecretDialog({
	open,
	onClose,
	secret,
	keyId,
}: {
	open: boolean;
	onClose: () => void;
	secret: string | null;
	keyId: string | null;
}): React.JSX.Element {
	const envSnippet = `AX_INTAKE_KEY_ID=${keyId ?? ""}\nAX_INTAKE_SECRET=${secret ?? ""}`;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			sx={{
				"& .MuiDialog-container": {
					"& .MuiPaper-root": {
						width: "100%",
					},
				},
			}}
		>
			<DialogTitle>
				Signing secret
				<IconButton color="inherit" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ py: 4!, pt: "8px !important" }}>
				<Stack spacing={2}>
					<Alert severity="warning">
						Copy this now. The API will not return it again - a lost secret has to be rotated.
					</Alert>

					<Alert severity="info">
						Keep the secret on your website&rsquo;s <strong>server</strong>. If it is shipped to the browser, anyone can
						use it to make your agent dial arbitrary numbers.
					</Alert>

					<Alert severity="success">
						Next: use <strong>Setup instructions</strong> on the key&rsquo;s row to copy a ready-made brief for your
						developer or coding assistant.
					</Alert>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Key id
						</Typography>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							<Box
								component="code"
								sx={{
									flex: 1,
									p: 1,
									borderRadius: 1,
									bgcolor: "background.level1",
									fontFamily: "monospace",
									fontSize: 13,
									wordBreak: "break-all",
								}}
							>
								{keyId}
							</Box>
							<IconButton onClick={() => copy(keyId ?? "", "Key id")} size="small">
								<CopyIcon />
							</IconButton>
						</Stack>
					</Box>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Secret
						</Typography>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
							<Box
								component="code"
								sx={{
									flex: 1,
									p: 1,
									borderRadius: 1,
									bgcolor: "background.level1",
									fontFamily: "monospace",
									fontSize: 13,
									wordBreak: "break-all",
								}}
							>
								{secret}
							</Box>
							<IconButton onClick={() => copy(secret ?? "", "Secret")} size="small">
								<CopyIcon />
							</IconButton>
						</Stack>
					</Box>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Environment variables
						</Typography>
						<Box
							component="pre"
							sx={{
								p: 1.5,
								borderRadius: 1,
								bgcolor: "background.level1",
								fontFamily: "monospace",
								fontSize: 13,
								overflowX: "auto",
								m: 0,
							}}
						>
							{envSnippet}
						</Box>
						<Button
							size="small"
							startIcon={<CopyIcon />}
							onClick={() => copy(envSnippet, "Environment variables")}
							sx={{ mt: 1 }}
						>
							Copy both
						</Button>
					</Box>
				</Stack>
			</DialogContent>
			<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)", py: 2, px: 3 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="primary" variant="contained" onClick={onClose}>
						I have stored the secret
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
